import React, { useState } from "react";
import RoleToggle from "../../components/RoleToggle";
import { useProfileData } from "../../apis/profileApis";
import {
  useMerchantAllowedShippingChannels,
  useConnectMerchant3PLCredentialsMutation,
} from "../../apis/integrationApis";
import toast from "react-hot-toast";
import {
  IconCheck,
  IconSearch,
  IconSettings,
  IconX,
  IconTruckDelivery,
  IconEye,
  IconEyeOff,
  IconFilter,
  IconChevronDown,
} from "@tabler/icons-react";

type CredentialField = {
  key: string;
  label: string;
  placeholder?: string;
  required: boolean;
  type?: "text" | "password" | "select";
  options?: { label: string; value: string }[];
};

const partnerFields: Record<string, CredentialField[]> = {
  Aramex: [
    { key: "AccountNumber", label: "Account Number", required: true, placeholder: "Enter Account Number" },
    { key: "AccountPin", label: "Account Pin", required: true, placeholder: "Enter Account Pin" },
    { key: "UserName", label: "Username", required: true, placeholder: "Enter Username" },
    { key: "Password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "AccountEntity", label: "Account Entity", required: true, placeholder: "Enter Account Entity" },
    { key: "AccountCountryCode", label: "Account Country Code", required: true, placeholder: "Enter Account Country Code" },
    {
      key: "PaymentType",
      label: "Payment Type",
      required: true,
      type: "select",
      placeholder: "Select Payment Type",
      options: [
        { label: "P (Prepaid)", value: "P" },
        { label: "C (Credit)", value: "C" },
        { label: "3 (Third Party)", value: "3" },
      ],
    },
    {
      key: "ProductGroup",
      label: "Product Group",
      required: true,
      type: "select",
      placeholder: "Select Product Group",
      options: [
        { label: "DOM (Domestic)", value: "DOM" },
        { label: "EXP (Express / International)", value: "EXP" },
      ],
    },
    {
      key: "ProductType",
      label: "Product Type",
      required: true,
      type: "select",
      placeholder: "Select Product Type",
      options: [
        { label: "CDS (Courier Domestic Service)", value: "CDS" },
        { label: "PDX (Priority Parcel Express)", value: "PDX" },
        { label: "PPX (Prepaid Parcel Express)", value: "PPX" },
        { label: "ONP (Overnight Parcel)", value: "ONP" },
        { label: "EPX (Economy Parcel Express)", value: "EPX" },
        { label: "DLV (Delivery)", value: "DLV" },
      ],
    },
  ],
  SMSA: [
    { key: "username", label: "Username", required: true, placeholder: "Enter SMSA username" },
    { key: "password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
  ],
  Naqel: [
    { key: "userName", label: "Username", required: true, placeholder: "Enter Naqel username" },
    { key: "password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "e.g. NL123456" },
    { key: "branchCode", label: "Branch Code", required: true, placeholder: "e.g. NL567899" },
  ],
  "Naqel Express": [
    { key: "userName", label: "Username", required: true, placeholder: "Enter Naqel username" },
    { key: "password", label: "Password", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "e.g. NL122212" },
    { key: "branchCode", label: "Branch Code", required: true, placeholder: "e.g. NL312542" },
  ],
  "J&T Express": [
    { key: "customerCode", label: "Customer Code", required: true, placeholder: "Enter customer code" },
    { key: "apiAccount", label: "API Account", required: true, placeholder: "Enter API account" },
    { key: "privateKey", label: "Private Key", required: true, type: "password", placeholder: "Leave blank to keep existing" },
  ],
  Zid: [
    { key: "clientId", label: "Client ID", required: true, placeholder: "e.g. 1234" },
    { key: "clientSecret", label: "Client Secret", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "appId", label: "App ID", required: true, placeholder: "e.g. 1234" },
  ],
  Shopify: [
    { key: "shopDomain", label: "Shop Domain", required: true, placeholder: "e.g. myshop.myshopify.com" },
    { key: "appId", label: "API Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "secretKey", label: "API Secret Key", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "accessToken", label: "Admin Access Token", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "apiVersion", label: "API Version", required: true, placeholder: "e.g. 2024-07" },
  ],
  Shoapify: [
    { key: "shopDomain", label: "Shop Domain", required: true, placeholder: "e.g. myshop.myshopify.com" },
    { key: "appId", label: "API Key", required: true, placeholder: "e.g. s3gh5hbhv6***********" },
    { key: "secretKey", label: "API Secret Key", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "accessToken", label: "Admin Access Token", required: true, type: "password", placeholder: "Leave blank to keep existing" },
    { key: "apiVersion", label: "API Version", required: true, placeholder: "e.g. 2024-07" },
  ],
};

const Integration: React.FC = () => {
  const [activeSection, setActiveSection] = useState("All Channels");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showPasswordMap, setShowPasswordMap] = useState<Record<string, boolean>>({});

  const { data: profile } = useProfileData();
  const clientId = profile?.id || profile?.clientId || "";

  const { data: allowedChannels, isLoading, refetch } = useMerchantAllowedShippingChannels(clientId);
  const connectMutation = useConnectMerchant3PLCredentialsMutation();

  const [selectedChannel, setSelectedChannel] = useState<any | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [environment, setEnvironment] = useState<"test" | "prod">("prod");

  const handleOpenConnect = (channel: any) => {
    setSelectedChannel(channel);
    setEnvironment(channel.environment || "prod");
    const companyName = channel.companyName || channel.title;
    const fields = partnerFields[companyName] || channel.credentialsFields || channel.credentialKeys || [];

    const existingCreds = channel.credentials || {};
    const initialData: Record<string, string> = {};

    fields.forEach((f: any) => {
      const key = typeof f === "string" ? f : f.key;
      initialData[key] = existingCreds[key] || "";
    });

    setFormData(initialData);
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedChannel) return;

    const connectionId = selectedChannel.connectionId || selectedChannel.id;

    connectMutation.mutate(
      {
        connectionId,
        credentials: {
          ...formData,
          clientId,
          channelId: selectedChannel.id,
        },
        environment,
      },
      {
        onSuccess: () => {
          toast.success(
            `${selectedChannel.companyName || selectedChannel.title} credentials saved successfully!`
          );
          setSelectedChannel(null);
          refetch();
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to save credentials");
        },
      }
    );
  };

  const togglePasswordVisibility = (key: string) => {
    setShowPasswordMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const channelsList = Array.isArray(allowedChannels) ? allowedChannels : [];

  const filteredChannels = channelsList.filter((ch: any) => {
    const name = (ch.companyName || ch.title || "").toLowerCase();
    const matchesSearch = name.includes(searchTerm.toLowerCase());
    const chType = (ch.type || ch.channelType || "").toLowerCase();

    const matchesType =
      activeSection === "All Channels" ||
      (activeSection === "Shipping" && (chType === "shipping" || !chType)) ||
      (activeSection === "Sales" && chType === "sales");

    const isConnected = ch.status === "Connected" || ch.isConnected;
    const matchesStatus =
      !statusFilter ||
      (statusFilter === "Connected" && isConnected) ||
      (statusFilter === "NotConnected" && !isConnected);

    return matchesSearch && matchesType && matchesStatus;
  });

  const getFieldsForSelectedChannel = (): CredentialField[] => {
    if (!selectedChannel) return [];
    const companyName = selectedChannel.companyName || selectedChannel.title;
    if (partnerFields[companyName]) {
      return partnerFields[companyName];
    }
    if (Array.isArray(selectedChannel.credentialsFields)) {
      return selectedChannel.credentialsFields.map((f: any) => ({
        key: f.key || f.name,
        label: f.label || f.name || f.key,
        required: Boolean(f.required),
        type: f.type || "text",
        placeholder: f.placeholder || "",
      }));
    }
    if (Array.isArray(selectedChannel.credentialKeys)) {
      return selectedChannel.credentialKeys.map((k: any) => ({
        key: typeof k === "string" ? k : k.key,
        label: typeof k === "string" ? k : k.label || k.key,
        required: true,
        type: "text",
        placeholder: `Enter ${typeof k === "string" ? k : k.label || k.key}`,
      }));
    }
    return [
      { key: "accountNumber", label: "Account Number", required: true, placeholder: "Enter Account Number" },
      { key: "apiKey", label: "API Key", required: true, placeholder: "Enter API Key" },
      { key: "apiSecret", label: "API Secret", required: false, type: "password", placeholder: "Leave blank to keep existing" },
    ];
  };

  const isChannelTab =
    activeSection === "All Channels" ||
    activeSection === "Shipping" ||
    activeSection === "Sales";

  const isSidebarOpen = selectedChannel !== null;
  const connectedCount = channelsList.filter((c: any) => c.status === "Connected" || c.isConnected).length;

  return (
    <div className="px-6 py-4 space-y-4">
      {/* Top Header RoleToggle Navigation matching Last-Mile-Frontend */}
      <div className="flex items-center justify-between">
        <RoleToggle
          options={[
            "All Channels",
            // "Shipping",
            // "Sales",
            // "Payment Gateway",
            // "Communication Hub",
          ]}
          active={activeSection}
          onChange={setActiveSection}
          activeClassName="bg-[#000000] text-[#ffffff] font-bold shadow-sm"
        />

        {/* Top-Right Summary Pill matching Last-Mile-Frontend Image 2 */}
        <div className="flex items-center gap-2 px-3 py-1 bg-[#FFFDF4] border border-amber-200/80 rounded-full text-xs font-semibold text-slate-700 shadow-2xs">
          <span>
            total: <span className="font-bold text-slate-900">{channelsList.length}</span>
          </span>
          <span className="w-px h-3 bg-amber-300"></span>
          <span>
            connected: <span className="font-bold text-emerald-600">{connectedCount}</span>
          </span>
          <span className="w-px h-3 bg-amber-300"></span>
          <span>
            request: <span className="font-bold text-amber-600">0</span>
          </span>
        </div>
      </div>

      {/* Main Grid Layout - Split between Channels Grid (col-span-7 or 12) & Side Panel (col-span-5) */}
      <div className="bg-[#FAF9F5]/70 backdrop-blur-xl transition-all duration-300 rounded-3xl w-full relative border border-slate-200/60 p-5 shadow-xs">
        {isChannelTab ? (
          <div className="grid grid-cols-12 gap-6">
            {/* Left Channels Grid Column */}
            <div
              className={`transition-all duration-300 ${
                isSidebarOpen ? "col-span-7" : "col-span-12"
              }`}
            >
              <div className="bg-white/80 rounded-3xl border border-slate-200/60 p-5 space-y-5">
                {/* Search & Filter bar top right */}
                <div className="flex items-center justify-end gap-2.5">
                  <div
                    onClick={() => setShowSearch(!showSearch)}
                    className={`flex items-center transition-all duration-300 border border-slate-200 rounded-lg bg-white shadow-2xs h-8 ${
                      showSearch ? "w-56 px-2" : "w-8 justify-center cursor-pointer"
                    }`}
                  >
                    <button
                      type="button"
                      className="text-slate-500 hover:text-slate-800 focus:outline-none"
                    >
                      <IconSearch size={16} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <input
                        type="text"
                        placeholder="Search channel..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        className="w-full ml-2 bg-transparent text-xs text-slate-800 outline-none border-none"
                      />
                    )}
                  </div>

                  <div className="relative">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 focus:outline-none shadow-2xs cursor-pointer appearance-none pr-7"
                    >
                      <option value="">All Statuses</option>
                      <option value="Connected">Connected</option>
                      <option value="NotConnected">Not Connected</option>
                    </select>
                    <IconFilter size={14} className="text-slate-400 absolute right-2 top-2 pointer-events-none" />
                  </div>
                </div>

                {/* Cards List Grid matching Last-Mile-Frontend Image 2 exact layout */}
                {isLoading ? (
                  <div className="grid 2xl:grid-cols-6 lg:grid-cols-4 md:grid-cols-3 grid-cols-2 gap-5 w-full">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse"></div>
                    ))}
                  </div>
                ) : filteredChannels.length === 0 ? (
                  <div className="text-center py-16 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                    <IconTruckDelivery size={36} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-600 font-semibold text-sm">No Integration Channels Found</p>
                    <p className="text-slate-400 text-xs mt-1">
                      Admin has not assigned any active channels matching your search.
                    </p>
                  </div>
                ) : (
                  <div
                    className={
                      isSidebarOpen
                        ? "grid 2xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-2 gap-5 w-full"
                        : "grid 2xl:grid-cols-6 lg:grid-cols-5 md:grid-cols-4 grid-cols-3 gap-5 w-full"
                    }
                  >
                    {filteredChannels.map((channel: any) => {
                      const companyName = channel.companyName || channel.title;
                      const isConnected = channel.status === "Connected" || channel.isConnected;
                      const isSelected = selectedChannel?.id === channel.id;

                      return (
                        <div
                          key={channel.id}
                          onClick={() => handleOpenConnect(channel)}
                          className="flex flex-col items-center text-center cursor-pointer group select-none"
                        >
                          {/* Logo Container matching system w-20 h-20 border-2 */}
                          <div
                            className={`w-20 h-20 rounded-2xl flex items-center justify-center border-2 transition-all p-2 bg-white relative ${
                              isSelected
                                ? "border-amber-400 ring-2 ring-amber-200 shadow-md"
                                : isConnected
                                ? "border-emerald-500 shadow-xs ring-2 ring-emerald-100"
                                : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                            }`}
                          >
                            {channel.companyLogo ? (
                              <img
                                src={channel.companyLogo}
                                alt={companyName}
                                className="w-full h-full object-contain p-1"
                              />
                            ) : (
                              <span className="font-extrabold text-slate-700 text-lg">
                                {(companyName || "3P")[0]}
                              </span>
                            )}

                            {isConnected && (
                              <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-xs">
                                <IconCheck size={12} stroke={3} />
                              </div>
                            )}
                          </div>

                          {/* Carrier Name */}
                          <span className="mt-2 font-bold text-xs text-slate-800 truncate max-w-full">
                            {companyName}
                          </span>

                          {/* Status / Connect Button */}
                          <span
                            className={`mt-1.5 text-[10px] font-bold px-3 py-0.5 rounded-full transition-all ${
                              isConnected
                                ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                                : "bg-black text-white hover:bg-slate-800"
                            }`}
                          >
                            {isConnected ? "Connected" : "Connect"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side Panel Column matching Image 2 */}
            {isSidebarOpen && (
              <div className="col-span-5">
                <div className="sticky top-4 bg-[#FFFDF6] border border-amber-200/70 rounded-3xl p-6 shadow-sm space-y-5 animate-in fade-in slide-in-from-right duration-200">
                  {/* Top Header Row of Side Panel */}
                  <div className="flex items-center justify-between border-b border-amber-100 pb-3">
                    <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                      {selectedChannel.companyName || selectedChannel.title}
                    </h2>

                    <div className="flex items-center gap-2">
                      {/* Yellow Primary Action Button top-right matching Image 2 */}
                      <button
                        type="button"
                        onClick={() => handleSubmit()}
                        disabled={connectMutation.isPending}
                        className="px-5 py-1.5 bg-[#FBD037] hover:bg-yellow-400 text-[#131327] font-extrabold text-xs rounded-lg transition-all shadow-2xs disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
                      >
                        {connectMutation.isPending ? (
                          <>
                            <div className="w-3 h-3 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
                            Saving...
                          </>
                        ) : selectedChannel.status === "Connected" || selectedChannel.isConnected ? (
                          "Update"
                        ) : (
                          "Connect"
                        )}
                      </button>

                      {/* Close X Button */}
                      <button
                        type="button"
                        onClick={() => setSelectedChannel(null)}
                        className="p-1.5 rounded-lg border border-slate-300/80 hover:bg-slate-100 text-slate-500 transition-colors"
                      >
                        <IconX size={18} />
                      </button>
                    </div>
                  </div>

                  {/* API ACCESS INFORMATION Banner & Toggle Pill matching Image 2 */}
                  <div className="flex items-center justify-between border-b border-amber-200/60 pb-3 pt-1">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-amber-100/80 text-slate-800">
                        <IconSettings size={16} stroke={2} />
                      </div>
                      <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-700">
                        API ACCESS INFORMATION
                      </span>
                    </div>

                    {/* Environment Pill Toggle Switch matching Image 2 */}
                    <div className="flex items-center bg-slate-200/70 p-0.5 rounded-full text-[10px] font-bold">
                      <button
                        type="button"
                        onClick={() => setEnvironment("test")}
                        className={`px-2.5 py-0.5 rounded-full transition-all ${
                          environment === "test"
                            ? "bg-amber-500 text-white shadow-2xs"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        TEST
                      </button>
                      <button
                        type="button"
                        onClick={() => setEnvironment("prod")}
                        className={`px-2.5 py-0.5 rounded-full transition-all ${
                          environment === "prod"
                            ? "bg-emerald-500 text-white shadow-2xs"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        PROD
                      </button>
                    </div>
                  </div>

                  {/* Credentials Form Inputs matching Image 2 exact field styling */}
                  <form onSubmit={handleSubmit} className="space-y-4 pt-1">
                    {getFieldsForSelectedChannel().map((field) => (
                      <div key={field.key} className="space-y-1">
                        <label className="block text-xs font-semibold text-slate-700">
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </label>

                        {field.type === "select" ? (
                          <div className="relative">
                            <select
                              value={formData[field.key] || ""}
                              onChange={(e) =>
                                setFormData({ ...formData, [field.key]: e.target.value })
                              }
                              className="w-full bg-[#FAF8F0] border border-slate-200/90 rounded-2xl px-4 py-2.5 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-amber-400 focus:bg-white focus:outline-none transition-all shadow-2xs appearance-none pr-9 cursor-pointer"
                            >
                              <option value="" disabled>
                                {field.placeholder || `Select ${field.label}`}
                              </option>
                              {field.options?.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <IconChevronDown
                              size={16}
                              className="absolute right-3.5 top-3 pointer-events-none text-slate-400"
                            />
                          </div>
                        ) : (
                          <div className="relative">
                            <input
                              type={
                                field.type === "password" && showPasswordMap[field.key]
                                  ? "text"
                                  : field.type || "text"
                              }
                              required={field.required && !formData[field.key] && selectedChannel.status !== "Connected"}
                              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                              value={formData[field.key] || ""}
                              onChange={(e) =>
                                setFormData({ ...formData, [field.key]: e.target.value })
                              }
                              className="w-full bg-[#FAF8F0] border border-slate-200/90 rounded-2xl px-4 py-2 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-amber-400 focus:bg-white focus:outline-none transition-all shadow-2xs"
                            />

                            {field.type === "password" && (
                              <button
                                type="button"
                                onClick={() => togglePasswordVisibility(field.key)}
                                className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 focus:outline-none"
                              >
                                {showPasswordMap[field.key] ? (
                                  <IconEyeOff size={16} />
                                ) : (
                                  <IconEye size={16} />
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : activeSection === "Payment Gateway" ? (
          <div className="text-center py-16 text-slate-500 font-medium text-sm">
            Payment Gateways configured for your merchant account will be displayed here.
          </div>
        ) : (
          <div className="text-center py-16 text-slate-500 font-medium text-sm">
            Communication Hub channels assigned to your account will be displayed here.
          </div>
        )}
      </div>
    </div>
  );
};

export default Integration;
